import { useEffect, useRef } from "react";

import type { QuoteBoardItem } from "~/shared/schemas/quote-board-schema.ts";

/**
 * Custom hook to manage expand icons visibility based on whether rows have children
 */
export const useExpandIconVisibility = (
	transformedGridData: (Omit<
		QuoteBoardItem,
		| "customerRate"
		| "carrierRate"
		| "totalWeight"
		| "margin"
		| "poNumber"
		| "maxLiability"
	> & {
		customerRate: number | null;
		carrierRate: number | null;
		totalWeight: number;
		margin: number | null;
		poNumber: number | null;
		maxLiability: string;
		hasChildren: boolean;
	})[],
	gridClassName = "quote-board-grid",
) => {
	const timeoutRef = useRef<NodeJS.Timeout>();
	const observerRef = useRef<MutationObserver>();
	const mountedRef = useRef(true);

	useEffect(() => {
		const hideExpandIconsForRowsWithoutChildren = () => {
			if (!mountedRef.current) return;

			const gridElement = document.querySelector(`.${gridClassName}`);
			if (!gridElement) return;

			const rows = gridElement.querySelectorAll('tr[role="row"]');

			for (const [index, row] of rows.entries()) {
				// Skip header row
				if (index === 0) continue;

				const dataIndex = index - 1; // Adjust for header row
				const rowData = transformedGridData[dataIndex];

				const expandCell = row.querySelector(".k-hierarchy-cell .k-icon");
				if (expandCell) {
					if (rowData && !rowData.hasChildren) {
						expandCell.classList.add("hidden-expand");
					} else {
						expandCell.classList.remove("hidden-expand");
					}
				}
			}
		};

		const scheduleHideIcons = () => {
			// Clear any existing timeout
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}

			// Use requestAnimationFrame for more reliable timing
			requestAnimationFrame(() => {
				timeoutRef.current = setTimeout(
					hideExpandIconsForRowsWithoutChildren,
					50,
				);
			});
		};

		// Set up mutation observer to watch for grid changes
		const gridElement = document.querySelector(`.${gridClassName}`);
		if (gridElement) {
			observerRef.current = new MutationObserver((mutations) => {
				// Check if any mutations affect the grid structure
				const hasStructuralChanges = mutations.some(
					(mutation) =>
						mutation.type === "childList" ||
						(mutation.type === "attributes" &&
							mutation.attributeName === "data-loading"),
				);

				if (hasStructuralChanges) {
					scheduleHideIcons();
				}
			});

			observerRef.current.observe(gridElement, {
				childList: true,
				subtree: true,
				attributes: true,
				attributeFilter: ["data-loading"],
			});
		}

		// Initial call
		scheduleHideIcons();

		return () => {
			mountedRef.current = false;
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
			if (observerRef.current) {
				observerRef.current.disconnect();
			}
		};
	}, [transformedGridData, gridClassName]);
};
